/**
 * OBR-166: homologació de proveïdors — la part PURA (sense I/O). Tall propi de Qualitat i APPCC: vegeu
 * docs/Pla_Implementacio_Qualitat_APPCC.md §2.
 *
 * QUÈ ÉS
 * Un proveïdor té un estat de homologació (`pending` per defecte, `approved`, `not_approved`, `suspended`) que
 * només canvia l'RPC `set_supplier_homologation`, i uns documents (registre sanitari, certificats) que poden
 * caducar. Aquí hi ha les regles que la UI, i més endavant l'App, comparteixen: quin canvi d'estat és vàlid,
 * quan s'ha d'avisar i en quin estat és un document.
 *
 * ELS AVISOS MAI BLOQUEGEN (CLAUDE.md arrel, pla §0.6). Aquestes funcions només diuen QUIN avís toca; qui les
 * crida el pinta i deixa continuar. Un proveïdor no homologat no impedeix fer la comanda ni registrar l'albarà.
 *
 * PURESA: sense rellotge implícit — `today` entra per paràmetre (`YYYY-MM-DD`), així corre igual al navegador,
 * al servidor i a l'App sense connexió.
 */

import { SUPPLIER_HOMOLOGATION_STATUSES, type SupplierHomologationStatus } from '../enums/index.js';

// ── Canvis d'estat ───────────────────────────────────────────────────────────────

/**
 * Els estats als quals es pot passar des de `from`: tots menys ell mateix (qualsevol → qualsevol), en l'ordre del
 * catàleg. No hi ha un flux obligatori: un proveïdor suspès pot tornar a homologat sense passar per res més, i un
 * d'homologat pot quedar no homologat. Allò que sí cal és el MOTIU (vegeu {@link homologationChange}).
 */
export function homologationTargets(
  from: SupplierHomologationStatus,
): readonly SupplierHomologationStatus[] {
  return SUPPLIER_HOMOLOGATION_STATUSES.filter((status) => status !== from);
}

/**
 * Què suposa demanar `to` a un proveïdor que ara és a `from`, amb el motiu escrit:
 *
 *  · `change`          — l'estat canvia i hi ha motiu: s'escriu a l'historial.
 *  · `reason_required` — l'estat canvia però el motiu és buit (o només espais): no es pot fer.
 *  · `notes_only`      — mateix estat: només es poden actualitzar les observacions, sense motiu ni historial
 *                        (és el que fa l'RPC amb el mateix estat).
 */
export type HomologationChange = 'change' | 'reason_required' | 'notes_only';

export function homologationChange(
  from: SupplierHomologationStatus,
  to: SupplierHomologationStatus,
  reason: string | null | undefined,
): HomologationChange {
  if (from === to) return 'notes_only';
  return (reason ?? '').trim() === '' ? 'reason_required' : 'change';
}

// ── Avisos ────────────────────────────────────────────────────────────────────────

/** Quin avís toca: el mateix estat del proveïdor que el motiva (mai `approved`, que no n'és cap). */
export type HomologationWarning = Exclude<SupplierHomologationStatus, 'approved'>;

/** On es mostra l'avís: a la comanda (i al seu enviament) o a la recepció. */
export type HomologationMoment = 'order' | 'reception';

/**
 * L'avís de COMANDA: tot proveïdor que no és `approved` en mereix un — també el pendent (encara no s'ha decidit
 * res i qui compra ho ha de saber). `null` = res a dir. Mai bloqueja.
 */
export function orderWarning(status: SupplierHomologationStatus): HomologationWarning | null {
  return status === 'approved' ? null : status;
}

/**
 * L'avís de RECEPCIÓ: només els proveïdors `not_approved` i `suspended`. El pendent NO avisa en rebre: el gènere
 * ja és a la porta i «encara no s'ha decidit» no és un motiu per fer-ne cas a qui descarrega. `null` = res a dir.
 * Mai bloqueja.
 */
export function receptionWarning(status: SupplierHomologationStatus): HomologationWarning | null {
  return status === 'not_approved' || status === 'suspended' ? status : null;
}

/**
 * Les dues regles darrere un sol nom, per a qui només sap «a quin moment sóc» (`order` per defecte).
 */
export function homologationWarning(
  status: SupplierHomologationStatus,
  moment: HomologationMoment = 'order',
): HomologationWarning | null {
  return moment === 'reception' ? receptionWarning(status) : orderWarning(status);
}

// ── Caducitat dels documents ──────────────────────────────────────────────────────

/** Els dies d'antelació amb què un document passa a «caduca aviat» si no se'n diu cap altre. */
export const DOCUMENT_EXPIRY_LEAD_DAYS = 30;

/**
 * · `none`     — el document no declara caducitat (un registre sanitari no sempre en porta) o la data no es pot llegir.
 * · `expired`  — la data de caducitat ja ha passat.
 * · `expiring` — caduca dins dels dies d'avís (el mateix dia de la caducitat encara hi compta: és vigent avui).
 * · `valid`    — encara queda temps.
 */
export type DocumentExpiry = 'valid' | 'expiring' | 'expired' | 'none';

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * El dia d'una data `YYYY-MM-DD` com a mil·lisegons UTC, o null si no és una data real (`2026-02-31` no ho és:
 * `Date` la deixaria rodar fins al març, i un document no pot caducar un dia que no existeix).
 */
function isoDayMs(value: string): number | null {
  const match = ISO_DATE.exec(value);
  if (match === null) return null;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const ms = Date.UTC(year, month - 1, day);
  const back = new Date(ms);
  if (back.getUTCFullYear() !== year || back.getUTCMonth() !== month - 1 || back.getUTCDate() !== day) {
    return null;
  }
  return ms;
}

/**
 * Dies que falten perquè caduqui (0 = caduca avui, negatiu = ja ha caducat), o null si no hi ha data o alguna de
 * les dues no es pot llegir. Ancorat a UTC: mai un salt per zona horària.
 */
export function documentDaysToExpiry(
  expiresOn: string | null | undefined,
  today: string,
): number | null {
  if (!expiresOn) return null;
  const expires = isoDayMs(expiresOn);
  const now = isoDayMs(today);
  if (expires === null || now === null) return null;
  return Math.round((expires - now) / 86_400_000);
}

/**
 * L'estat de caducitat d'un document, derivat de la data cada vegada que es mira (cap columna, cap procés nocturn).
 *
 * @param expiresOn  Caducitat (`YYYY-MM-DD`), o null quan no en declara.
 * @param today      El dia d'avui (`YYYY-MM-DD`). Explícit: cap rellotge amagat.
 * @param leadDays   Dies d'avís. Negatiu o no finit ⇒ 0 (només marca el que ja ha caducat): una configuració
 *                   estranya no pot amagar un document caducat.
 */
export function documentExpiry(
  expiresOn: string | null | undefined,
  today: string,
  leadDays: number = DOCUMENT_EXPIRY_LEAD_DAYS,
): DocumentExpiry {
  const days = documentDaysToExpiry(expiresOn, today);
  if (days === null) return 'none';
  if (days < 0) return 'expired';
  const lead = Number.isFinite(leadDays) && leadDays > 0 ? Math.trunc(leadDays) : 0;
  return days <= lead ? 'expiring' : 'valid';
}

/**
 * Les dates d'un document són coherents? Una caducitat anterior a l'emissió és gairebé segur un error de teclat.
 * `null` = res a dir (també quan falta alguna de les dues o no es pot llegir: això no és incoherència, és buit).
 */
export type DocumentDatesError = 'expires_before_issued';

export function documentDatesError(
  issuedOn: string | null | undefined,
  expiresOn: string | null | undefined,
): DocumentDatesError | null {
  if (!issuedOn || !expiresOn) return null;
  const issued = isoDayMs(issuedOn);
  const expires = isoDayMs(expiresOn);
  if (issued === null || expires === null) return null;
  return expires < issued ? 'expires_before_issued' : null;
}
